import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import nl from 'date-fns/locale/nl';
import moment from 'moment';

import * as sharedActions from '@/shared/Actions';
import { EVENTS_TYPES } from '@/shared/Constants';
import useResponsive from '@/services/hocs/useResponsive';
import * as activityActions from '../store/actions';
import {
	isDateToday,
	MONTH_FORMAT_DATE,
	PARSE_FORMAT_DATE,
	VIEW_FORMAT_DATE,
} from '../store/constants';
import { getNextReportActivityDate } from '../helpers';

import Button from '@/components/button/Button';

import 'react-datepicker/dist/react-datepicker.css';
import '../styles/activity-datepicker.scss';

import type { RootState } from '@/rootState';

registerLocale('nl', nl);
moment.locale('nl');

interface CustomInputProps {
	value?: string;
	onClick?: () => void;
	onClickButton?: () => void;
}

const CustomInput = forwardRef<HTMLButtonElement, CustomInputProps>(
	({ value, onClick, onClickButton }, ref) => {
		const { t } = useTranslation();

		const title = useMemo(() => {
			let date = value ? moment(value, 'MM/DD/YYYY').format(VIEW_FORMAT_DATE) : '';
			date = date.replace('.', '');
			return date.charAt(0).toUpperCase() + date.slice(1);
		}, [value]);

		const handleClick = () => {
			onClick && onClick();
			onClickButton && onClickButton();
		};

		return (
			<button
				type="button"
				className="activity-datepicker__button"
				onClick={handleClick}
				ref={ref}
			>
				<span className="text">{title}</span>
				<span className="icon icon-chevron-down" />
			</button>
		);
	},
);

interface DatePickerHeaderProps {
	date: Date;
	decreaseMonth: () => void;
	increaseMonth: () => void;
	prevMonthButtonDisabled: boolean;
	nextMonthButtonDisabled: boolean;
}

interface RenderCustomHeaderProps extends DatePickerHeaderProps {
	onClickReset: () => void;
}

const RenderCustomHeader = ({
	date,
	decreaseMonth,
	increaseMonth,
	prevMonthButtonDisabled,
	nextMonthButtonDisabled,
	onClickReset,
}: RenderCustomHeaderProps) => {
	const { isMobile } = useResponsive();
	const monthTitle = moment(date).format(MONTH_FORMAT_DATE).replace('.', '');

	return (
		<div className="activity-datepicker__header">
			<strong className="title">{monthTitle}</strong>
			<div className="activity-datepicker__header-buttons">
				{isMobile ? (
					<>
						<Button
							onClick={decreaseMonth}
							disabled={prevMonthButtonDisabled}
							type="secondary"
							rounded
							iconName="icon-chevron-left"
						/>
						<Button
							onClick={increaseMonth}
							disabled={nextMonthButtonDisabled}
							type="secondary"
							rounded
							iconName="icon-chevron-right"
						/>
						<Button
							onClick={onClickReset}
							type="primary"
							color="blue"
							rounded
							iconName="icon-undo-reverse"
						/>
					</>
				) : (
					<>
						<button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
							<span className="icon icon-chevron-left" />
						</button>
						<button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
							<span className="icon icon-chevron-right" />
						</button>
					</>
				)}
			</div>
		</div>
	);
};

interface ActivityDatePickerProps {
	className?: string;
	onChangeDate?: (date: string) => void;
}

const ActivityDatePicker = ({ className, onChangeDate }: ActivityDatePickerProps) => {
	const { t } = useTranslation();
	const { isMobile } = useResponsive();
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);

	const activeDate = useSelector((state: RootState) => state.activityReducer.activeDate);
	const playedDays = useSelector((state: RootState) => state.activityReducer.playedDays);

	const datepickerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		dispatch(activityActions.getPlayedDaysRequest());
	}, []);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (open && datepickerRef.current && datepickerRef.current.contains(e.target as Node))
				return;
			setOpen(false);
		};

		open && document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [open]);

	const highlighted = useMemo(() => {
		return playedDays.map((day) => moment(day, PARSE_FORMAT_DATE).toDate());
	}, [playedDays]);

	const handleChangeDate = (date: Date) => {
		const formatDate = moment(date).format('YYYY-MM-DD');
		const nextDate = getNextReportActivityDate(formatDate);
		dispatch(
			activityActions.getReportActivityRequest(nextDate, 6, () => {
				setTimeout(() => {
					onChangeDate && onChangeDate(formatDate);
					dispatch(activityActions.setActiveDate(date));
					dispatch(activityActions.setNextDate(nextDate));
				}, 100);
			}),
		);

		setOpen(false);

		if (!isDateToday(date)) {
			dispatch(
				sharedActions.sendEventRequest({
					event_name: EVENTS_TYPES.REVIEW_PICKED_DATE_IN_THE_PAST,
					meta_data: {
						date: moment(date).format(PARSE_FORMAT_DATE),
					},
				}),
			);
		}
	};

	const handleClickReset = () => {
		handleChangeDate(new Date());
	};

	const handleChangeMonth = (value: Date) => {
		const twoMonthsAgo = moment(value)
			.startOf('month')
			.subtract(2, 'months')
			.format('YYYY-MM-DD');
		dispatch(activityActions.getPlayedDaysRequest(twoMonthsAgo));
	};

	const handleClickInput = () => {
		setOpen(true);
	};

	return (
		<div className={classNames('activity-datepicker', className)}>
			<div className="activity-datepicker__wrapper" ref={datepickerRef}>
				<DatePicker
					locale="nl"
					open={open}
					maxDate={new Date()}
					selected={activeDate}
					onChange={handleChangeDate}
					customInput={<CustomInput onClickButton={handleClickInput} />}
					renderCustomHeader={({
						date,
						decreaseMonth,
						increaseMonth,
						prevMonthButtonDisabled,
						nextMonthButtonDisabled,
					}: DatePickerHeaderProps) => (
						<RenderCustomHeader
							date={date}
							decreaseMonth={decreaseMonth}
							increaseMonth={increaseMonth}
							prevMonthButtonDisabled={prevMonthButtonDisabled}
							nextMonthButtonDisabled={nextMonthButtonDisabled}
							onClickReset={handleClickReset}
						/>
					)}
					highlightDates={highlighted}
					fixedHeight
					onMonthChange={handleChangeMonth}
					withPortal={isMobile}
					inline={!isMobile}
				/>
				{!isMobile && !isDateToday(activeDate) && (
					<div className="activity-datepicker__footer">
						<Button
							type="icon-link"
							className="reset-day"
							title={t('activity.today')}
							iconName="icon-undo-reverse"
							onClick={handleClickReset}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default ActivityDatePicker;
